// src/config.js
/**
 * Central configuration, read once from the environment.
 *
 * Every value falls back to the literal that was previously hardcoded in the
 * components, so the app behaves identically whether or not a .env file is
 * present. Set values in `.env` (committed defaults) or `.env.local`
 * (git-ignored, wins over `.env`) -- see `.env.example` for the full list.
 *
 * IMPORTANT: this is a Create React App frontend. Every REACT_APP_* variable is
 * inlined into the JavaScript bundle at build time, so nothing here is a
 * runtime secret -- anyone can read these values from the shipped bundle.
 * Moving a value into .env makes it *configurable per environment*, not
 * private. The auth values below are marked accordingly.
 *
 * Environment variables always arrive as strings, so each reader below coerces
 * to the type the application code expects. Passing a malformed value falls
 * back to the default rather than propagating NaN/undefined into the UI.
 */

const env = process.env;

/** Number, falling back to `fallback` when unset or not finite. */
const num = (raw, fallback) => {
  if (raw === undefined || raw === null || String(raw).trim() === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
};

/** Trimmed string, falling back when unset or empty. */
const str = (raw, fallback) => {
  if (raw === undefined || raw === null || String(raw).trim() === '') return fallback;
  return String(raw).trim();
};

/** Comma-separated list -> array of trimmed, non-empty strings. */
const list = (raw, fallback) => {
  if (raw === undefined || raw === null || String(raw).trim() === '') return fallback;
  const parts = String(raw).split(',').map((s) => s.trim()).filter(Boolean);
  return parts.length > 0 ? parts : fallback;
};

/** JSON literal -> parsed value; malformed JSON falls back rather than throwing. */
const json = (raw, fallback) => {
  if (raw === undefined || raw === null || String(raw).trim() === '') return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed === null ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
};

// ==========================================
// API
// ==========================================

// Base URL for every backend call. A trailing slash is enforced because call
// sites concatenate the endpoint name directly onto it.
const rawApiUrl = str(env.REACT_APP_API_URL, 'https://options.healthsaving.life/api/');
export const API_URL = rawApiUrl.endsWith('/') ? rawApiUrl : `${rawApiUrl}/`;

// ==========================================
// AUTH -- configurable, NOT secret (see the note at the top of this file)
// ==========================================

// Credentials the client-side login modal accepts. This gate only decides
// which UI is shown; the API performs its own authorisation.
export const PREDEFINED_CREDENTIALS = json(env.REACT_APP_PREDEFINED_CREDENTIALS, [
  { username: 'admin', password: 'Me6OngDR4C' },
  { username: 'user@gmail.com', password: '123456' },
]);

// Shared secret used to verify the SHA-256 signature on hand-off URL tokens.
export const TOKEN_SECRET_KEY = str(env.REACT_APP_TOKEN_SECRET_KEY, 'options_builder_secret_2026');

// How far a token's timestamp may drift from now and still be accepted.
export const TOKEN_WINDOW_SECONDS = num(env.REACT_APP_TOKEN_WINDOW_SECONDS, 600);

// Lifetime of the "remember me" credential cookies, in days.
export const COOKIE_EXPIRY_DAYS = num(env.REACT_APP_COOKIE_EXPIRY_DAYS, 365);

// ==========================================
// PRICING MODEL DEFAULTS
// ==========================================

// Risk-free rate shown in the UI control, as a percentage (3.79 == 3.79%).
export const DEFAULT_RISK_FREE_RATE = num(env.REACT_APP_DEFAULT_RISK_FREE_RATE, 3.79);

// Risk-free rate used by the forecast engine, as a decimal (0.0379 == 3.79%).
// Separate from the value above because the forecast screen has no UI control
// for it and consumes the decimal form directly.
export const FORECAST_RISK_FREE_RATE = num(env.REACT_APP_FORECAST_RISK_FREE_RATE, 0.0379);

// Shares per option contract -- the standard US equity option multiplier.
export const CONTRACT_SIZE = num(env.REACT_APP_CONTRACT_SIZE, 100);

// ==========================================
// ACCOUNTS
// ==========================================

// Account selected on first load, before the account list has been fetched.
export const DEFAULT_ACCOUNT = str(env.REACT_APP_DEFAULT_ACCOUNT, 'Walleyhouse');

// Accounts assumed by the bar chart when the account list is still empty.
export const FALLBACK_ACCOUNTS = list(env.REACT_APP_FALLBACK_ACCOUNTS, [
  'Walleyhouse',
  'Manufacturers',
  'Cash',
]);

// Fixed per-account series colours, so an account keeps its colour between
// sessions. Accounts not listed here fall back to white at the call site.
export const ACCOUNT_COLORS = json(env.REACT_APP_ACCOUNT_COLORS, {
  Walleyhouse: '#1b9fdf',
  Manufacturers: '#00c93a',
  Cash: '#ff9800',
});

// ==========================================
// FORECAST SCREEN
// ==========================================

// The only symbol for which the beta-weighted payoff series is meaningful;
// it stays hidden for every other symbol.
export const BETA_SYMBOL = str(env.REACT_APP_BETA_SYMBOL, 'QQQ');

// Seed assumptions for a freshly built forecast timeline, both percentages.
export const FORECAST_DEFAULT_IV = num(env.REACT_APP_FORECAST_DEFAULT_IV, 20);
export const FORECAST_DEFAULT_PRICE_MOVE = num(env.REACT_APP_FORECAST_DEFAULT_PRICE_MOVE, 5);

// ==========================================
// UI
// ==========================================

// Date-scrubber cap used when the furthest expiry is missing or already past.
export const DEFAULT_MAX_DAYS = num(env.REACT_APP_DEFAULT_MAX_DAYS, 30);
